import { ManifestationStep } from "@/types";

interface StepHelp {
  title: string;
  purpose: string;
  howToAnswer: string;
  examples: string[];
}

export const stepHelpContent: Partial<Record<ManifestationStep, StepHelp>> = {
  "welcome": {
    title: "Getting Started",
    purpose: "This is where you choose whether to work with a saved affirmation or create a brand new one.",
    howToAnswer: "Type 'existing' to pick from your saved affirmations, or 'new' to create a fresh one. If you see a numbered list, you can also type the number to select one directly.",
    examples: [
      "new",
      "existing",
      "1 (to select the first saved affirmation)",
    ],
  },
  "affirmation-choice": {
    title: "Share Your Goal",
    purpose: "This is where you share what you want to manifest. Be specific - the clearer your desire, the more powerful the process.",
    howToAnswer: "Write a clear, specific goal. Don't worry about phrasing it perfectly - we'll refine it in the next step.",
    examples: [
      "I want to earn $10,000 per month",
      "I want to find my soulmate",
      "I want to launch my own business",
      "I want to be completely debt-free",
    ],
  },
  "goal-confirmation": {
    title: "Present Tense Rephrasing",
    purpose: "Writing your goal in present tense tricks your subconscious into believing it's already real. This is a core manifestation principle - 'act as if.'",
    howToAnswer: "Rewrite your goal as if it's already true right now. Start with 'I am', 'I have', 'My life is', etc.",
    examples: [
      "I am earning $10,000 every month with ease",
      "I am in a deeply loving relationship with my soulmate",
      "My business is thriving and growing every day",
      "I am completely debt-free and financially abundant",
    ],
  },
  "goal-setting": {
    title: "Choose Your Cycles",
    purpose: "Repetition deepens the emotional connection to your manifestation. Each cycle takes you through writing, feeling, visualizing, and reflecting - building stronger alignment each time.",
    howToAnswer: "Pick a number between 1 and 12. Start with 2-3 if you're new. More cycles = deeper alignment, but quality matters more than quantity.",
    examples: [
      "2",
      "3",
      "5",
    ],
  },
  "cycle-setup": {
    title: "Write Your Affirmation",
    purpose: "Physically writing (typing) your affirmation engages your conscious mind and reinforces the neural pathways associated with your desire. Each time you write it, it becomes more real.",
    howToAnswer: "Type out your affirmation exactly as shown, or in your own words. The act of writing it is what matters - feel the truth of it as you type each word.",
    examples: [
      "My net worth is now over $1M!",
      "I am living in my dream home",
      "I am healthy, vibrant, and full of energy",
    ],
  },
  "present-tense": {
    title: "Connect With the Feeling",
    purpose: "Emotion is the language of manifestation. When you feel the emotions of your desire as if it's already real, you raise your vibration to match that reality.",
    howToAnswer: "Close your eyes for a moment. Imagine your affirmation is 100% true right now. What emotions come up? Name them and describe them.",
    examples: [
      "I feel a deep sense of freedom and relief - like a weight has been lifted",
      "Joy and excitement, like I can't stop smiling",
      "Peace and security - I feel completely safe and provided for",
      "Confidence and pride - I did it and I know it",
    ],
  },
  "feeling-state": {
    title: "Visualize Your Reality",
    purpose: "Visualization creates a detailed mental blueprint of your manifested reality. The more vivid and specific, the more your subconscious accepts it as real.",
    howToAnswer: "Describe what you see, hear, and experience in this reality. Who's around you? What's your environment like? What conversations are happening?",
    examples: [
      "I'm sitting in my home office with floor-to-ceiling windows. My partner brings me coffee and we talk about our weekend plans.",
      "I'm walking through my front door and everything feels calm and spacious. My kids are laughing in the garden.",
      "I'm checking my phone and seeing the balance. I feel calm - not excited, just peaceful. It's normal now.",
    ],
  },
  "respond": {
    title: "Rate How Real It Feels",
    purpose: "This honest check-in reveals the gap between where you are and where you want to be. Awareness of this gap is actually what closes it - you can't bridge what you can't see.",
    howToAnswer: "Give a number from 1-10. 1 means it feels completely distant and unlikely. 10 means it feels absolutely true right now. Be honest - there's no wrong answer.",
    examples: [
      "7 - it feels close, like it could happen any day",
      "4 - I can see it but it still feels like a dream",
      "9 - it honestly feels like it's already mine",
      "5 - somewhere in the middle, getting closer",
    ],
  },
  "reality-check": {
    title: "Reality Check & Tips",
    purpose: "Based on your rating, you'll receive personalized tips to close the gap between where you are and your manifested reality. This step helps you understand what to focus on.",
    howToAnswer: "Read the tips, take them in, then simply say you're ready to continue.",
    examples: [
      "Ready",
      "Let's go",
      "Yes, let's continue",
      "I'm ready for the next step",
    ],
  },
  "summary": {
    title: "Name Your Predominant Emotion",
    purpose: "Identifying your core emotion after the cycles reveals your manifestation frequency. This emotion is the magnetic force that draws your desire toward you.",
    howToAnswer: "Name the single strongest emotion you're feeling right now, as if your manifestation is already real. One word or a short phrase.",
    examples: [
      "Gratitude",
      "Deep peace",
      "Freedom",
      "Excitement and joy",
      "Relief",
    ],
  },
  "core-feeling": {
    title: "Find It In Your Current Life",
    purpose: "This is the manifestation bridge. When you recognize where this feeling already exists in your life, you multiply the vibration that attracts your desire. You're already closer than you think.",
    howToAnswer: "Think about moments in your current life that already give you this same feeling - even in small ways.",
    examples: [
      "I feel this gratitude when I wake up next to my partner",
      "I get this freedom feeling on long drives with the windows down",
      "I feel this peace during my morning meditation",
      "Playing with my dog gives me this same pure joy",
    ],
  },
};
